import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { usePlan } from '../../../contexts/PlanContext';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useRadarData } from '../../../hooks/useRadarData';
import RadarHeader from '../../../components/radar/RadarHeader';
import RadarAccounts from '../../../components/radar/RadarAccounts';
import RadarContacts from '../../../components/radar/RadarContacts';
import RadarTranscriptAnalyzer from '../../../components/radar/RadarTranscriptAnalyzer';
import RadarBuyerCards from '../../../components/radar/RadarBuyerCards';
import './Radar.css';

const Radar = () => {
  const { user } = useAuth();
  const { planType } = usePlan();
  const { trackModuleAccess } = useAnalytics();
  const location = useLocation();
  
  // Use custom hooks for state management
  const {
    accounts,
    tableLoading,
    accountRequest,
    accountsPagination,
    contacts,
    contactsTableLoading,
    contactRequest,
    contactsPagination,
    transcripts,
    industries,
    dealStages,
    loadAccounts,
    loadContacts,
    loadTranscripts,
    loadIndustries,
    loadDealStages
  } = useRadarData(user);

  // Debug: Log accounts when they change
  useEffect(() => {
    console.log('🔍 Radar.js - Accounts state changed:', {
      accounts,
      accountsLength: accounts?.length || 0,
      isArray: Array.isArray(accounts),
      sampleAccount: accounts?.[0]
    });
  }, [accounts]);
  
  // Local state
  const [loading, setLoading] = useState(false); // Start with false to show structure immediately
  const [error, setError] = useState('');
  
  // Navigation state
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Filter state (shared across components)
  const [selectedCompany, setSelectedCompany] = useState('');
  const [contactSearchTerm, setContactSearchTerm] = useState('');
  const [transcriptCompanyId, setTranscriptCompanyId] = useState('');
  const [transcriptContactNames, setTranscriptContactNames] = useState('');

  // Get unique companies for filters
  const companies = useMemo(() => {
    const companySet = new Set();
    accounts.forEach(account => {
      if (account.companyName || account.name) {
        companySet.add(account.companyName || account.name);
      }
    });
    contacts.forEach(contact => {
      if (contact.company || contact.companyName) {
        companySet.add(contact.company || contact.companyName);
      }
    });
    return Array.from(companySet).sort();
  }, [accounts, contacts]);

  // Get current view from URL
  const getCurrentView = () => {
    const path = location.pathname;
    if (path.includes('/contacts')) return 'Contacts';
    if (path.includes('/transcripts')) return 'Transcript Analyzer';
    if (path.includes('/buyer-cards')) return 'Buyer Cards';
    return 'Accounts'; // default
  };

  const currentView = getCurrentView();

  // Reset filters when view changes
  useEffect(() => {
    setSelectedCompany('');
    setContactSearchTerm('');
    setTranscriptCompanyId('');
    setTranscriptContactNames('');
  }, [currentView]);

  // Load initial data in background
  useEffect(() => {
    const initializeData = async () => {
      try {
        setError('');
        
        // Load data in background without blocking the UI
        // Each component will handle its own loading states
        Promise.allSettled([
          loadAccounts(),
          loadContacts(),
          loadTranscripts(),
          loadIndustries(),
          loadDealStages()
        ]).catch(err => {
          console.error('Error loading initial data:', err);
          setError('Failed to load some data. Please try refreshing.');
        });
      } catch (err) {
        console.error('Error loading initial data:', err);
        setError('Failed to load some data. Please try refreshing.');
      }
    };

    if (user) {
      initializeData();
    }
  }, [user, loadAccounts, loadContacts, loadTranscripts, loadIndustries, loadDealStages]);

  // Track module access
  useEffect(() => {
    trackModuleAccess('radar', {
      plan: planType,
      has_accounts: accounts.length > 0,
      has_contacts: contacts.length > 0,
      has_transcripts: transcripts.length > 0
    });
  }, []);

  // Handle click outside menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="radar-container" style={{
      height: '100vh',
      background: '#f8fafc',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <RadarHeader 
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        view={currentView}
        menuRef={menuRef}
        companies={companies}
        selectedCompany={selectedCompany}
        onCompanyChange={(e) => {
          const value = e.target ? e.target.value : e;
          setSelectedCompany(value);
          if (!value) {
            setContactSearchTerm('');
          }
        }}
        contactSearchTerm={contactSearchTerm}
        onContactSearchChange={(value) => setContactSearchTerm(value)}
        accounts={accounts}
        contacts={contacts}
        transcriptCompanyId={transcriptCompanyId}
        onTranscriptCompanyChange={(value) => {
          setTranscriptCompanyId(value || '');
          setTranscriptContactNames('');
        }}
        transcriptContactNames={transcriptContactNames}
        onTranscriptContactChange={(value) => setTranscriptContactNames(value)}
      />

      {/* Main Content */}
      <div className="radar-main-content" style={{
        flex: 1,
        padding: '0 20px 20px 20px',
        overflow: 'hidden'
      }}>
        {/* Show error message if there's an error */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            border: '1px solid #e5e7eb',
            flexDirection: 'column',
            gap: '16px',
            textAlign: 'center',
            padding: '24px',
            marginBottom: '20px'
          }}>
            <h3 style={{ color: '#dc2626', margin: 0, fontSize: '18px' }}>Error Loading Data</h3>
            <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>{error}</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Retry
            </button>
          </div>
        )}
        
        <Routes>
          <Route index element={<Navigate to="accounts" replace />} />
          <Route path="accounts" element={
            <RadarAccounts 
              accounts={accounts}
              industries={industries}
              dealStages={dealStages}
              loading={tableLoading}
              pagination={accountsPagination}
              requestParams={accountRequest}
              onLoadAccounts={loadAccounts}
            />
          } />
          <Route path="contacts" element={
            <RadarContacts 
              contacts={contacts}
              selectedCompany={selectedCompany}
              onCompanyChange={(e) => {
                const value = e.target ? e.target.value : e;
                setSelectedCompany(value);
                if (!value) {
                  setContactSearchTerm('');
                }
              }}
              contactSearchTerm={contactSearchTerm}
              onContactSearchChange={setContactSearchTerm}
              loading={contactsTableLoading}
              pagination={contactsPagination}
              requestParams={contactRequest}
              onLoadContacts={loadContacts}
            />
          } />
          <Route path="transcripts" element={
            <RadarTranscriptAnalyzer 
              accounts={accounts}
              contacts={contacts}
              industries={industries}
              dealStages={dealStages}
              transcriptCompanyId={transcriptCompanyId}
              onTranscriptCompanyChange={(value) => setTranscriptCompanyId(value)}
              transcriptContactNames={transcriptContactNames}
              onTranscriptContactChange={(value) => setTranscriptContactNames(value)}
            />
          } />
          <Route path="buyer-cards" element={
            <RadarBuyerCards 
              accounts={accounts}
              contacts={contacts}
              selectedCompany={selectedCompany}
              onCompanyChange={(e) => {
                const value = e.target ? e.target.value : e;
                setSelectedCompany(value);
                if (!value) {
                  setContactSearchTerm('');
                }
              }}
              contactSearchTerm={contactSearchTerm}
              onContactSearchChange={setContactSearchTerm}
            />
          } />
        </Routes>
      </div>
    </div>
  );
};

export default Radar; 
